import { SHARE_DOMAIN_SUBDOMAIN_KEY } from '@backoffice-frontend/common'
import { ENV, Environment } from '@backoffice-frontend/environment'
import { Textfield } from '@backoffice-frontend/forms'
import { useLocalStorage } from '@backoffice-frontend/hooks'
import { MoiaLogo } from '@backoffice-frontend/patterns'
import {
  Button,
  Callout,
  Card,
  FormFieldValidation,
  Text,
} from '@moia-dev/pace-web'
import { type FormEvent, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

const baseDomains: Partial<Record<Environment, string>> = {
  [Environment.development]: '.backoffice.dev.moia-group.io',
  [Environment.integration]: '.backoffice.int.moia-group.io',
  [Environment.production]: '.backoffice.moia-group.io',
  [Environment['production.us-east-1']]: '.backoffice.us.moia-group.io',
}

const identityProviderApiBaseUrl: Partial<Record<Environment, string>> = {
  [Environment.development]:
    'https://cognito-identity-provider.backoffice.dev.moia-group.io',
  [Environment.integration]:
    'https://cognito-identity-provider.backoffice.int.moia-group.io',
  [Environment.production]:
    'https://cognito-identity-provider.backoffice.prd.moia-group.io',
  [Environment['production.us-east-1']]:
    'https://cognito-identity-provider.backoffice.prd.us-east-1.moia-group.io',
}

const baseDomain = baseDomains[ENV]
const shareDomain = baseDomain ? `share${baseDomain}` : undefined
const apiBaseUrl = identityProviderApiBaseUrl[ENV]

export const ShareDomainRedirect = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [savedSubdomain, setSavedSubdomain] = useLocalStorage<string | null>(
    SHARE_DOMAIN_SUBDOMAIN_KEY,
    null,
  )
  const isShareDomain = window.location.hostname === shareDomain

  const redirectToSubdomain = (subdomain: string) => {
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash
    window.location.href = `https://${subdomain}${baseDomain}${currentPath}`
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!email) return

    setError('')
    try {
      const response = await fetch(
        `${apiBaseUrl}/backoffice-url?email=${encodeURIComponent(email)}`,
      )

      if (!response.ok) {
        throw new Error('Failed to find organization for this email')
      }

      const data = (await response.json()) as { subdomain: string }

      if (data.subdomain) {
        setSavedSubdomain(data.subdomain)
        redirectToSubdomain(data.subdomain)
        return
      }

      setError(t('loginView_shareDomainRedirect_noOrganizationFound'))
    } catch (err) {
      console.error('Error redirecting:', err)
      setError(t('loginView_shareDomainRedirect_error'))
    }
  }

  if (!isShareDomain) {
    return children
  }

  if (savedSubdomain) {
    redirectToSubdomain(savedSubdomain)
    return null
  }

  return (
    <>
      <title>{t('loginView_shareDomainRedirect_title')}</title>
      <div
        css={{
          minHeight: '100vh',
          backgroundSize: 'cover',
          alignItems: 'flex-start',
          justifyItems: 'center',
          backgroundImage: `url(/assets/images/login.png)`,
          display: 'grid',
        }}
      >
        <MoiaLogo
          css={theme => ({
            width: '160px',
            marginTop: theme.spacing(6),
            fill: theme.palette.common.white,
          })}
        />
        <Card
          css={theme => ({
            padding: theme.spacing(3),
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
          })}
          outline
        >
          <Callout variant="neutral" hideDismiss>
            <Text>{t('loginView_shareDomainRedirect_callout')}</Text>
          </Callout>
          <form
            onSubmit={handleSubmit}
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            })}
          >
            <Textfield
              label={t('common_email_label')}
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {error && (
              <FormFieldValidation type="error">{error}</FormFieldValidation>
            )}
            <Button
              type="submit"
              label={t('loginView_shareDomainRedirect_button')}
              disabled={!email}
            />
          </form>
        </Card>
      </div>
    </>
  )
}
